import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  Building,
  MapPin,
  PlusCircle,
  Briefcase,
  Home,
  Upload,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const ModalCreateBusinessLocationForm = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const [companyID, setCompanyID] = useState("");
  const [type, setType] = useState("");
  const [companyBusinessLocationName, setCompanyBusinessLocationName] = useState("");
  const [companyBusinessLocationAddress, setCompanyBusinessLocationAddress] = useState("");
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getRegisteredCompanyOnUploadForm');
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    switch (id) {
      case "companyID":
        setCompanyID(value);
        break;
      case "type":
        setType(value);
        break;
      case "companyBusinessLocationName":
        setCompanyBusinessLocationName(value);
        break;
      case "companyBusinessLocationAddress":
        setCompanyBusinessLocationAddress(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    const formData = new FormData();
    formData.append("companyID", companyID);
    formData.append("businessType", type);
    formData.append("companyBusinessLocationName", companyBusinessLocationName);
    formData.append("companyBusinessLocationAddress", companyBusinessLocationAddress);
    formData.append("apiType", 'registerCompanyBusinessLocation');

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      const data = response.data;
      
      if (data === 'Upload Completed Successfully' || data === '') {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Business location registered successfully!' 
        });
        // Reset form
        setCompanyID("");
        setType("");
        setCompanyBusinessLocationName("");
        setCompanyBusinessLocationAddress("");
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: data 
        });
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Error: Please check your internet connection. If this persists, contact support.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = companyID && type && companyBusinessLocationName && companyBusinessLocationAddress;

  return (
    <>
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 pl-64">
        <div className="p-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Register Business Location</h1>
                <p className="text-gray-600 mt-2">Add new business locations for your properties</p>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Companies</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{companies.length}</p>
                </div>
                <Building className="w-12 h-12 text-blue-400" />
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              {/* Form Header */}
              <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center space-x-3">
                  <PlusCircle className="w-6 h-6 text-blue-500" />
                  <h2 className="text-xl font-bold text-gray-900">New Business Location</h2>
                </div>
                <p className="text-gray-600 mt-2">Fill in the details below to register a new business location</p>
              </div>

              {/* Form Content */}
              <div className="p-8">
                {submitStatus.message && (
                  <div className={`mb-6 p-4 rounded-xl ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center">
                      {submitStatus.type === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                      )}
                      <p className={submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                        {submitStatus.message}
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Company Selection */}
                  <div>
                    <label htmlFor="companyID" className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center">
                        <Building className="w-4 h-4 mr-2 text-gray-400" />
                        Select Company
                      </div>
                    </label>
                    <select
                      id="companyID"
                      value={companyID}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      required
                    >
                      <option value="">Choose a company...</option>
                      {companies.map(company => (
                        <option key={company.companyID} value={company.companyID}>
                          {company.companyName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Business Type */}
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center">
                        <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
                        Business Type
                      </div>
                    </label>
                    <select
                      id="type"
                      value={type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      required
                    >
                      <option value="">Select type...</option>
                      <option value="Plots">Plots</option>
                      <option value="Houses">Houses</option>
                    </select>
                  </div>

                  {/* Business Location Name */}
                  <div>
                    <label htmlFor="companyBusinessLocationName" className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                        Business Location Name
                      </div>
                    </label>
                    <input
                      type="text"
                      id="companyBusinessLocationName"
                      value={companyBusinessLocationName}
                      onChange={handleInputChange}
                      placeholder="e.g., Main Office, Branch Location, etc."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      required
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <label htmlFor="companyBusinessLocationAddress" className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center">
                        <Home className="w-4 h-4 mr-2 text-gray-400" />
                        Address
                      </div>
                    </label>
                    <textarea
                      id="companyBusinessLocationAddress"
                      value={companyBusinessLocationAddress}
                      onChange={handleInputChange}
                      placeholder="Enter complete address including street, city, and country"
                      rows="4"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="pt-6">
                    <button
                      type="submit"
                      disabled={!isFormValid || isLoading}
                      className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                        isFormValid && !isLoading
                          ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg hover:shadow-xl'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                          Registering...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <Upload className="w-5 h-5 mr-2" />
                          Register Business Location
                        </div>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Form Footer */}
              <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-4 h-4 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Important Note</p>
                      <p className="text-xs text-gray-500">All fields are required for registration</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Step 1 of 1</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Tips */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Building className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="font-medium text-gray-900">Company Selection</h3>
                </div>
                <p className="text-sm text-gray-600">Select the parent company for this location from your registered companies.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <Briefcase className="w-5 h-5 text-green-500" />
                  </div>
                  <h3 className="font-medium text-gray-900">Business Type</h3>
                </div>
                <p className="text-sm text-gray-600">Choose whether this location handles Plots, Houses, or both types of properties.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-purple-500" />
                  </div>
                  <h3 className="font-medium text-gray-900">Location Details</h3>
                </div>
                <p className="text-sm text-gray-600">Provide a clear name and complete address for easy identification and navigation.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalCreateBusinessLocationForm;